/**
 * useHighlightSuggestions Hook
 *
 * Fetches category-specific highlight suggestions from API
 * Provides loading state and error handling
 *
 * Epic: MVP 1.20.1-S - Highlight Suggestions System
 */

import { useState, useEffect } from 'react';
import apiRequest from '../../services/api';

/**
 * Fetch highlight suggestions for a specific L1 category
 *
 * @param {number} categoryL1Id - L1 category ID (37-48)
 * @returns {Object} { suggestions, loading, error }
 */
export function useHighlightSuggestions(categoryL1Id) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip if no category ID provided
    if (!categoryL1Id) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    let isMounted = true;

    async function fetchSuggestions() {
      try {
        setLoading(true);
        setError(null);

        const result = await apiRequest(
          `/api/v1/experiences/highlight-suggestions?categoryL1Id=${categoryL1Id}`
        );

        if (isMounted) {
          setSuggestions(result.data.suggestions || []);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    fetchSuggestions();

    return () => {
      isMounted = false;
    };
  }, [categoryL1Id]);

  return { suggestions, loading, error };
}
